import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type {
  GenerationImpactLayoutHead,
  GenerationImpactScenarioSnapshot,
  TestFitAuthorityRef,
} from '../../types/generationImpact';
import { isStaleSed } from '../sedStaleness';
import { resolveGenerationImpactSedAuthority } from './sedAuthority';
import {
  createGenerationImpactParcelBoundarySnapshot,
  sameGenerationImpactParcelBoundary,
} from './boundaryAuthority';
import { sameGenerationImpactLayoutHeadAndPublication } from './integrity';
import {
  assessGenerationImpactTestFitApplicability,
  generationImpactTestFitScopeFromSite,
  type GenerationImpactTestFitApplicability,
} from './testFitApplicability';
import { createTestFitAuthorityRef } from './testFitAuthority';
import { generationScenarioProjectInputStalenessReason } from './generationProjectInputSync';

export type TestFitWorkflowCode =
  | 'feature-unavailable'
  | 'scenario-required'
  | 'save-required'
  | 'scenario-conflict'
  | 'promotion-required'
  | 'publication-required'
  | 'sed-rebuild-required'
  | 'sed-rebuilding'
  | 'sed-failed'
  | 'ready';

export interface TestFitWorkflowReadiness {
  ready: boolean;
  code: TestFitWorkflowCode;
  title: string;
  message: string;
  applicability: GenerationImpactTestFitApplicability;
  /** Present only when every persisted authority identity is current. */
  authority?: TestFitAuthorityRef;
  /** Grid-only/legacy Test Fit remains usable without Generation & Impact. */
  generationImpactRequired: boolean;
}

export interface EvaluatePersistedTestFitWorkflowOptions {
  /** Feature/runtime availability is supplied by the owning UI/server. */
  generationImpactEnabled?: boolean;
  /**
   * Force the strict chain for an explicitly selected optional Test Fit.
   * Omit to derive the rule from canonical site intent.
   */
  requireGenerationImpact?: boolean;
}

export interface WorkbenchTestFitWorkflowInput {
  site: SiteEntity;
  sed: SiteEvidenceDocument | null | undefined;
  scenario: GenerationImpactScenarioSnapshot | null;
  inputIsSaved: boolean;
  remoteScenarioChanged: boolean;
  promotedScenario?: GenerationImpactScenarioSnapshot | null;
  layoutHead?: GenerationImpactLayoutHead | null;
  sedRebuildRequested?: boolean;
}

function sameScenarioRevision(
  left: GenerationImpactScenarioSnapshot | null | undefined,
  right: GenerationImpactScenarioSnapshot | null | undefined,
): boolean {
  return Boolean(
    left
      && right
      && left.id === right.id
      && left.updatedAt === right.updatedAt
      && left.inputHash === right.inputHash
      && left.resultHash === right.resultHash
      && left.result.modelVersion === right.result.modelVersion
      && (left.promotedAt ?? undefined) === (right.promotedAt ?? undefined),
  );
}

function layoutMatchesScenario(
  head: GenerationImpactLayoutHead | null | undefined,
  scenario: GenerationImpactScenarioSnapshot | null | undefined,
): boolean {
  return Boolean(
    head
      && scenario
      && head.scenarioId === scenario.id
      && head.scenarioName === scenario.name
      && head.scenarioUpdatedAt === scenario.updatedAt
      && head.inputHash === scenario.inputHash
      && head.resultHash === scenario.resultHash
      && head.modelVersion === scenario.result.modelVersion,
  );
}

function blocked(
  code: Exclude<TestFitWorkflowCode, 'ready'>,
  title: string,
  message: string,
  applicability: GenerationImpactTestFitApplicability,
  generationImpactRequired = applicability.required,
): TestFitWorkflowReadiness {
  return {
    ready: false,
    code,
    title,
    message,
    applicability,
    generationImpactRequired,
  };
}

/**
 * Verify the persisted authority chain required by Test Fit.
 *
 * This is deliberately stricter than the general report preflight: Test Fit
 * consumes published geometry, so it may not fall back to live Project Intent,
 * a mutable scenario document, or a prior SED revision. Every hash/version in
 * the promoted scenario, active layout head, and frozen SED authority must
 * describe the same immutable revision.
 */
export function evaluatePersistedTestFitWorkflow(
  site: SiteEntity,
  sed: SiteEvidenceDocument | null | undefined,
  options: EvaluatePersistedTestFitWorkflowOptions = {},
): TestFitWorkflowReadiness {
  const applicability = assessGenerationImpactTestFitApplicability(
    generationImpactTestFitScopeFromSite(site),
  );
  if (site.generationAssessmentSelection?.kind === 'grid-led-standby-planning') return blocked('publication-required', 'Physical assessment required', 'Selected static planning does not qualify equipment layout or a physical Test Fit. Select a qualified engineering/layout assessment before this output.', applicability, true);
  const promoted = site.promotedGenerationScenarioSnapshot;
  const projectInputsStale = generationScenarioProjectInputStalenessReason(site.projectIntent, promoted, site.generationProjectInputBinding);
  if (projectInputsStale) return blocked('scenario-conflict', 'Recalculate changed project inputs', projectInputsStale, applicability, true);
  const promotedId = site.projectIntent?.promotedGenerationScenarioId;
  const hasGenerationImpactWorkflow = Boolean(
    promoted
      || promotedId
      || site.generationImpactLayoutHead,
  );
  // Callers may opt an additional report into the strict chain, but may never
  // override canonical on-site intent back to false.
  const generationImpactRequired = applicability.required
    || options.requireGenerationImpact === true;

  if (applicability.status === 'conflict') {
    return blocked(
      'scenario-conflict',
      'Resolve conflicting power intent',
      `${applicability.reasons.join(' ')} Align Project Intent before preparing Test Fit.`,
      applicability,
      true,
    );
  }

  if (options.generationImpactEnabled === false && (generationImpactRequired || hasGenerationImpactWorkflow)) {
    return blocked(
      'feature-unavailable',
      'Generation & Impact is unavailable',
      'This Test Fit requires the Generation & Impact workflow, but that capability is not available for this site or environment.',
      applicability,
      generationImpactRequired,
    );
  }

  // Backwards compatibility: Test Fit existed before Generation & Impact.
  // Sites that have never entered this workflow keep their existing report
  // path only when on-site generation is not required. Once intent requires
  // physical generation/storage, or any authority field exists, the chain is
  // strict and may never silently fall back to legacy mutable inputs.
  if (!hasGenerationImpactWorkflow) {
    if (generationImpactRequired) {
      // The terminating verb must be one THIS evaluator can observe.
      // `hasGenerationImpactWorkflow` reads only the promoted snapshot, the
      // promoted id and the layout head — and saving a scenario writes none of
      // them (`saveGenerationImpactScenarioServer` touches only `updatedAt` /
      // `lastDataChangeAt` on the site). Promotion is what clears this state.
      // Saying "create and solve" alone left a user who did exactly that
      // staring at a byte-identical card, with no indication that Promote was
      // the real next click.
      return blocked(
        'scenario-required',
        'Configure Generation & Impact',
        'Create, solve and promote a Generation & Impact scenario for the declared on-site power path before generating Test Fit. Saving the scenario alone does not clear this step — promote it to Project Intent.',
        applicability,
        true,
      );
    }
    return {
      ready: true,
      code: 'ready',
      title: 'Test Fit is ready',
      message: applicability.status === 'grid-only'
        ? 'The declared power path is grid-only, so Generation & Impact is not applicable to this Test Fit.'
        : 'Generation & Impact is not yet applicable; this Test Fit keeps the legacy evidence path while the power strategy remains undecided.',
      applicability,
      generationImpactRequired: false,
    };
  }
  if (!promoted
    || !promotedId
    || promoted.id !== promotedId
    || site.projectIntent?.promotedGenerationInputHash !== promoted.inputHash
    || site.projectIntent?.promotedGenerationResultHash !== promoted.resultHash
    || !promoted.promotedAt
    || site.projectIntent?.promotedGenerationPromotedAt !== promoted.promotedAt) {
    return blocked(
      'promotion-required',
      'Promote a generation scenario',
      'Test Fit needs one saved Generation & Impact scenario frozen into Project Intent.',
      applicability,
      generationImpactRequired,
    );
  }

  const head = site.generationImpactLayoutHead;
  if (!head || !layoutMatchesScenario(head, promoted)) {
    return blocked(
      'publication-required',
      'Publish the promoted layout',
      'Publish a Site Map layout from the exact promoted scenario revision before generating Test Fit.',
      applicability,
      generationImpactRequired,
    );
  }

  if (site.sedStatus === 'bootstrapping') {
    return blocked(
      'sed-rebuilding',
      'SED rebuild in progress',
      'The layout is published. Wait for the Site Evidence Document rebuild to finish before generating Test Fit.',
      applicability,
      generationImpactRequired,
    );
  }
  if (site.sedStatus === 'failed') {
    return blocked(
      'sed-failed',
      'SED rebuild failed',
      site.sedFailureReason
        ? `Retry the SED rebuild. Last failure: ${site.sedFailureReason}`
        : 'Retry the SED rebuild before generating Test Fit.',
      applicability,
      generationImpactRequired,
    );
  }
  if (site.sedStatus !== 'complete' || !site.sedId || !sed) {
    return blocked(
      'sed-rebuild-required',
      'Rebuild the Site Evidence Document',
      'Freeze the promoted scenario and published layout into a completed SED revision before generating Test Fit.',
      applicability,
      generationImpactRequired,
    );
  }
  if (site.sedId !== sed.id
    || site.sedVersion == null
    || sed.version !== site.sedVersion
    || isStaleSed(site)) {
    return blocked(
      'sed-rebuild-required',
      'SED revision is stale',
      'Rebuild the SED so the loaded evidence version matches the site pointer and current publishing contract.',
      applicability,
      generationImpactRequired,
    );
  }

  const authority = resolveGenerationImpactSedAuthority(sed);
  if (authority.status !== 'valid') {
    return blocked(
      'sed-rebuild-required',
      'SED publishing authority is missing',
      authority.status === 'invalid'
        ? `Rebuild the SED to replace its invalid Generation & Impact authority: ${authority.reason}`
        : 'Rebuild the SED to freeze the promoted scenario and published layout authority.',
      applicability,
      generationImpactRequired,
    );
  }

  const frozen = authority.snapshot;
  const frozenScenario = frozen.scenarioSnapshot;
  const frozenLayout = frozen.layoutPublication;
  if (!sameScenarioRevision(frozenScenario, promoted)
    || !sameGenerationImpactLayoutHeadAndPublication(head, frozenLayout)
    || frozen.provenance.scenarioId !== promoted.id
    || frozen.provenance.inputHash !== promoted.inputHash
    || frozen.provenance.resultHash !== promoted.resultHash
    || frozen.provenance.modelVersion !== promoted.result.modelVersion
    || frozen.provenance.promotedAt !== promoted.promotedAt
    || frozen.provenance.layoutPublicationId !== head.publicationId
    || frozen.provenance.layoutVersion !== head.version
    || frozen.provenance.layoutHash !== head.layoutHash
    || frozen.provenance.layoutPublishedAt !== head.publishedAt) {
    return blocked(
      'sed-rebuild-required',
      'SED authority is behind the published layout',
      'Rebuild the SED so its frozen scenario, layout version, and integrity hashes match the active Site Map publication.',
      applicability,
      generationImpactRequired,
    );
  }

  try {
    const currentBoundary = createGenerationImpactParcelBoundarySnapshot(site);
    if (!sameGenerationImpactParcelBoundary(frozen.parcelBoundary, currentBoundary)) {
      return blocked(
        'sed-rebuild-required',
        'Parcel boundary changed after the SED rebuild',
        'Rebuild the SED so Test Fit uses the current canonical parcel geometry, union acreage, and published layout in one authority tuple.',
        applicability,
        generationImpactRequired,
      );
    }
  } catch (error) {
    return blocked(
      'sed-rebuild-required',
      'Parcel boundary cannot be verified',
      `Fix the parcel boundary and rebuild the SED before generating Test Fit. ${(error as Error)?.message ?? ''}`.trim(),
      applicability,
      generationImpactRequired,
    );
  }

  const authorityRef = createTestFitAuthorityRef(site, sed);
  if (!authorityRef) {
    return blocked(
      'sed-rebuild-required',
      'Test Fit authority cannot be verified',
      'Rebuild the SED so the promoted scenario, published layout, parcel boundary, and SED identity form one current authority tuple.',
      applicability,
      generationImpactRequired,
    );
  }
  return {
    ready: true,
    code: 'ready',
    title: 'Test Fit is ready',
    message: `Scenario “${promoted.name}”, layout v${head.version}, and SED v${sed.version} are frozen to one authority tuple.`,
    applicability,
    authority: authorityRef,
    generationImpactRequired,
  };
}

/** Add the workbench's dirty/saved selection checks ahead of persistence checks. */
export function evaluateWorkbenchTestFitWorkflow({
  site,
  sed,
  scenario,
  inputIsSaved,
  remoteScenarioChanged,
  promotedScenario = site.promotedGenerationScenarioSnapshot,
  layoutHead = site.generationImpactLayoutHead,
  sedRebuildRequested = false,
}: WorkbenchTestFitWorkflowInput): TestFitWorkflowReadiness {
  const applicability = assessGenerationImpactTestFitApplicability(
    generationImpactTestFitScopeFromSite(site),
  );
  if (site.generationAssessmentSelection?.kind === 'grid-led-standby-planning') return blocked('publication-required', 'Physical assessment required', 'Selected static planning does not qualify equipment layout or a physical Test Fit. Historical v1 snapshots remain readable; select one through normal promotion to restore its active authority.', applicability, true);
  if (remoteScenarioChanged) {
    return blocked(
      'scenario-conflict',
      'Reload the saved scenario',
      'This scenario changed elsewhere. Reload its latest revision or clone the preserved draft before continuing.',
      applicability,
      true,
    );
  }
  if (!scenario || !inputIsSaved) {
    return blocked(
      'save-required',
      'Save the scenario revision',
      'Test Fit can only use an immutable saved Generation & Impact snapshot.',
      applicability,
      true,
    );
  }
  // The workbench advances the selected immutable candidate. A stale active
  // promotion must not prevent its current replacement from being promoted;
  // persisted/report readiness still checks the active authority separately.
  const projectInputsStale = generationScenarioProjectInputStalenessReason(site.projectIntent, scenario, site.generationProjectInputBinding);
  if (projectInputsStale) return blocked('scenario-conflict', 'Recalculate changed project inputs', projectInputsStale, applicability, true);
  if (!promotedScenario || !sameScenarioRevision(promotedScenario, scenario)) {
    return blocked(
      'promotion-required',
      'Promote this saved revision',
      'Promote the selected scenario to Project Intent before publishing its layout.',
      applicability,
      true,
    );
  }
  if (!layoutHead || !layoutMatchesScenario(layoutHead, scenario)) {
    return blocked(
      'publication-required',
      'Publish this promoted layout',
      'Review and publish a new immutable Site Map version from this exact promoted scenario.',
      applicability,
      true,
    );
  }
  if (sedRebuildRequested && site.sedStatus !== 'failed' && site.sedStatus !== 'complete') {
    return blocked(
      'sed-rebuilding',
      'SED rebuild requested',
      'The rebuild is running in the background. Test Fit will unlock only after the live SED authority matches this layout.',
      applicability,
      true,
    );
  }

  return evaluatePersistedTestFitWorkflow(
    {
      ...site,
      promotedGenerationScenarioSnapshot: promotedScenario,
      generationImpactLayoutHead: layoutHead,
      projectIntent: site.projectIntent
        ? {
            ...site.projectIntent,
            promotedGenerationScenarioId: promotedScenario.id,
            promotedGenerationInputHash: promotedScenario.inputHash,
            promotedGenerationResultHash: promotedScenario.resultHash,
            ...(promotedScenario.promotedAt
              ? { promotedGenerationPromotedAt: promotedScenario.promotedAt }
              : {}),
          }
        : site.projectIntent,
    },
    sed,
  );
}

export function testFitWorkflowBlockMessage(
  site: SiteEntity,
  sed: SiteEvidenceDocument | null | undefined,
  options?: EvaluatePersistedTestFitWorkflowOptions,
): string | null {
  const readiness = evaluatePersistedTestFitWorkflow(site, sed, options);
  return readiness.ready ? null : `${readiness.title}: ${readiness.message}`;
}

export const generationImpactWorkflowInternals = {
  sameScenarioRevision,
  layoutMatchesScenario,
};

export {
  createTestFitAuthorityRef,
  createTestFitAuthorityRefFromAuthority,
  requireTestFitAuthorityRef,
  sameTestFitAuthorityRef,
  testFitAuthorityStalenessReason,
} from './testFitAuthority';
export {
  assessGenerationImpactTestFitApplicability,
  generationImpactTestFitScopeFromSite,
  requiresGenerationImpactTestFit,
} from './testFitApplicability';
